import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useQueryGetUserProfile } from './react-query/useData';
import { getUserDetails } from '../utils';

/**
 * Custom hook to manage profile data with automatic refresh capabilities
 * This hook provides a centralized way to manage user profile data
 * and ensures the profile page always displays the latest information
 */
export const useProfileData = () => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get initial data from localStorage
  const userDetails = getUserDetails();
  const userToken = userDetails?.token;

  // React Query hook for fetching profile data
  const { 
    data: profileResponse, 
    isLoading, 
    error, 
    refetch: refetchProfile,
    isFetching 
  } = useQueryGetUserProfile(userToken, navigate, toast);

  // Update profile data when API response changes
  useEffect(() => {
    if (profileResponse?.data?.data) {
      const freshProfileData = profileResponse.data.data;
      setProfileData(freshProfileData);
      
      // Update localStorage with fresh data; prefer API hasCompletedTour (source of truth)
      const currentUserData = JSON.parse(localStorage.getItem('user')) || {};
      const token = currentUserData?.token;
      const hasCompletedTour =
        typeof freshProfileData?.hasCompletedTour === 'boolean'
          ? freshProfileData.hasCompletedTour
          : currentUserData?.hasCompletedTour;

      const updatedUserData = {
        ...freshProfileData,
        token,
        ...(typeof hasCompletedTour === 'boolean' && { hasCompletedTour }),
      };
      
      localStorage.setItem('user', JSON.stringify(updatedUserData));
    } else if (userDetails) {
      // Fallback to localStorage data if API hasn't loaded yet
      setProfileData(userDetails);
    }
  }, [profileResponse, userDetails]);

  // Function to manually refresh profile data
  const refreshProfile = useCallback(async () => {
    if (!userToken) return;
    
    setIsRefreshing(true);
    try {
      await refetchProfile();
    } catch (error) {
      console.error('Error refreshing profile:', error);
      toast.error('Failed to refresh profile data');
    } finally {
      setIsRefreshing(false);
    }
  }, [userToken, refetchProfile]);

  // Function to update profile data after successful profile updates
  const updateProfileData = useCallback((updatedData) => {
    if (updatedData) {
      setProfileData(prevData => {
        const newData = {
          ...prevData,
          ...updatedData
        };
        
        // Update localStorage while preserving client-only fields (e.g. hasCompletedTour)
        const currentUserData = JSON.parse(localStorage.getItem('user')) || {};
        const token = currentUserData?.token;
        const hasCompletedTour = currentUserData?.hasCompletedTour;
        
        const updatedUserData = {
          ...newData,
          token,
          ...(typeof hasCompletedTour === 'boolean' && { hasCompletedTour }),
        };
        
        localStorage.setItem('user', JSON.stringify(updatedUserData));
        
        return newData;
      });
    }
  }, []);

  return {
    profileData: profileData || userDetails,
    isLoading: isLoading || isFetching,
    isRefreshing,
    error,
    refreshProfile,
    updateProfileData,
    refetchProfile
  };
};
